import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Avatar,
  Badge,
  Box,
  Button as ChakraButton,
  Flex,
  Grid,
  GridItem,
  HStack,
  Icon,
  IconButton,
  Select,
  Skeleton,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  Document,
  Page,
  StyleSheet,
  Text as PdfText,
  View,
  pdf,
} from '@react-pdf/renderer';
import { formatDistanceToNowStrict, isPast, isToday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiAlertTriangle,
  FiArrowDown,
  FiArrowUp,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiEdit3,
  FiEye,
  FiFilter,
  FiFolder,
  FiLoader,
  FiPlus,
  FiPrinter,
  FiRefreshCcw,
  FiSearch,
  FiTrash2,
  FiUser,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import Button from '../components/Form/Button';
import Input from '../components/Form/Input';
import Pagination from '../components/Pagination';
import HeaderSideBar from '../components/HeaderSideBar';
import TaskDetailDrawer from '../components/task/TaskDetailDrawer';
import { PermissionByIdDTO, TaskPropsDTO, TaskSummaryDTO } from '../dtos';
import { useAuth } from '../contexts/AuthContext';
import { useDebounce } from '../hooks/useDebounce';
import api from '../services/api';
import { getFormatDate } from '../utils/date';
import { paginationArray } from '../utils/pdfPagination';

type SelectOption = {
  label: string;
  value: string;
};

type TaskTabKey = 'all' | 'BACKLOG' | 'FAZENDO' | 'CONCLUIDO' | 'overdue';

const PER_PAGE = 8;

const statusOptions = [
  { label: 'Todos os status', value: 'all' },
  { label: 'Pendentes', value: 'BACKLOG' },
  { label: 'Em andamento', value: 'FAZENDO' },
  { label: 'Concluídas', value: 'CONCLUIDO' },
  { label: 'Atrasadas', value: 'overdue' },
  { label: 'Não concluídas', value: 'open' },
];

const priorityOptions = [
  { label: 'Todas as prioridades', value: 'all' },
  { label: 'Alta', value: 'ALTA' },
  { label: 'Média', value: 'MEDIA' },
  { label: 'Baixa', value: 'BAIXA' },
];

const sortOptions = [
  { label: 'Prazo mais próximo', value: 'deadline:asc' },
  { label: 'Prazo mais distante', value: 'deadline:desc' },
  { label: 'Maior prioridade', value: 'priority:asc' },
  { label: 'Menor prioridade', value: 'priority:desc' },
  { label: 'Mais recentes', value: 'date:desc' },
  { label: 'Mais antigas', value: 'date:asc' },
];

const statusMeta = {
  BACKLOG: {
    label: 'Pendente',
    colorScheme: 'yellow',
  },
  FAZENDO: {
    label: 'Em andamento',
    colorScheme: 'blue',
  },
  CONCLUIDO: {
    label: 'Concluída',
    colorScheme: 'green',
  },
};

const priorityMeta = {
  ALTA: {
    label: 'Alta',
    colorScheme: 'red',
  },
  MEDIA: {
    label: 'Média',
    colorScheme: 'orange',
  },
  BAIXA: {
    label: 'Baixa',
    colorScheme: 'gray',
  },
};

const pdfStyles = StyleSheet.create({
  page: {
    paddingTop: 20,
    paddingHorizontal: 18,
    paddingBottom: 24,
    fontSize: 9,
  },
  header: {
    marginBottom: 10,
  },
  headerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 6,
  },
  officeName: {
    fontSize: 10,
    fontWeight: 700,
    textAlign: 'right',
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: 2,
  },
  subtitle: {
    fontSize: 9,
    textAlign: 'center',
  },
  table: {
    borderWidth: 1,
    borderColor: '#222',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#222',
  },
  lastTableRow: {
    borderBottomWidth: 0,
  },
  tableHeaderCell: {
    fontSize: 8.5,
    fontWeight: 700,
    paddingHorizontal: 5,
    paddingVertical: 6,
    justifyContent: 'center',
  },
  tableCell: {
    fontSize: 8,
    paddingHorizontal: 5,
    paddingVertical: 8,
    justifyContent: 'center',
  },
  colSupporter: {
    width: '18%',
    borderRightWidth: 1,
    borderRightColor: '#222',
  },
  colStatus: {
    width: '8%',
    borderRightWidth: 1,
    borderRightColor: '#222',
  },
  colTitle: {
    width: '21%',
    borderRightWidth: 1,
    borderRightColor: '#222',
  },
  colDescription: {
    width: '32%',
    borderRightWidth: 1,
    borderRightColor: '#222',
  },
  colPhone: {
    width: '10%',
    borderRightWidth: 1,
    borderRightColor: '#222',
  },
  colAddress: {
    width: '11%',
  },
  footer: {
    marginTop: 8,
    fontSize: 8,
    color: '#475569',
    textAlign: 'right',
  },
});

function normalizePdfText(value?: string | null) {
  if (!value) {
    return '-';
  }

  return value
    .replace(/\s+/g, ' ')
    .trim();
}

function stripHtmlFromPdfText(value?: string | null) {
  if (!value) {
    return '-';
  }

  return normalizePdfText(
    value
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/p>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'"),
  );
}

function getTaskStatusLabel(status?: string | null) {
  if (!status) {
    return '-';
  }

  return status.toUpperCase();
}

function getTaskAddress(task: TaskPropsDTO) {
  const voter = task.voter;

  if (!voter) {
    return '-';
  }

  const segments = [
    voter.street,
    voter.address_number,
    voter.neighborhood,
    voter.city,
  ]
    .map(item => normalizePdfText(item))
    .filter(item => item !== '-');

  return segments.length ? segments.join(', ') : '-';
}

function SummaryCard({
  label,
  value,
  helper,
  icon,
  accent,
}: {
  label: string;
  value: number;
  helper: string;
  icon: any;
  accent: string;
}) {
  return (
    <Box
      rounded="3xl"
      borderWidth="1px"
      borderColor="gray.100"
      bg="white"
      px={5}
      py={5}
      shadow="sm"
    >
      <Flex justify="space-between" align="flex-start" gap={4}>
        <Box>
          <Text fontSize="sm" fontWeight="semibold" color="gray.500">
            {label}
          </Text>
          <Text mt={2} fontSize="3xl" fontWeight="bold" color="gray.800">
            {value}
          </Text>
          <Text mt={1} fontSize="sm" color="gray.500">
            {helper}
          </Text>
        </Box>
        <Flex
          h={12}
          w={12}
          align="center"
          justify="center"
          rounded="2xl"
          bg={accent}
          color="white"
        >
          <Icon as={icon} boxSize={5} />
        </Flex>
      </Flex>
    </Box>
  );
}

function StatusBadge({ status }: { status: string }) {
  const meta = statusMeta[status as keyof typeof statusMeta];

  if (!meta) {
    return <Badge rounded="full">{status}</Badge>;
  }

  return (
    <Badge colorScheme={meta.colorScheme} rounded="full" px={3} py={1}>
      {meta.label}
    </Badge>
  );
}

function PriorityBadge({ priority }: { priority: string }) {
  const meta = priorityMeta[priority as keyof typeof priorityMeta];

  if (!meta) {
    return <Badge rounded="full">{priority}</Badge>;
  }

  return (
    <Badge colorScheme={meta.colorScheme} variant="subtle" rounded="full" px={3} py={1}>
      {meta.label}
    </Badge>
  );
}

function CurrentTasksPdf({
  officeName,
  tasks,
}: {
  officeName: string;
  tasks: TaskPropsDTO[];
}) {
  const pages = paginationArray(tasks, 12) as TaskPropsDTO[][];

  return (
    <Document>
      {(pages.length ? pages : [[]]).map((pageTasks, pageIndex) => (
        <Page key={`pdf-page-${pageIndex + 1}`} size="A4" style={pdfStyles.page}>
          <View style={pdfStyles.header}>
            <View style={pdfStyles.headerTop}>
              <PdfText> </PdfText>
              <PdfText style={pdfStyles.officeName}>{officeName}</PdfText>
            </View>
            <PdfText style={pdfStyles.title}>Lista de Demandas</PdfText>
            <PdfText style={pdfStyles.subtitle}>
              Emitido em {getFormatDate(new Date(), 'dd/MM/yyyy')}
            </PdfText>
          </View>

          <View style={pdfStyles.table}>
            <View style={pdfStyles.tableRow}>
              <View style={[pdfStyles.tableHeaderCell, pdfStyles.colSupporter]}>
                <PdfText>Apoiador</PdfText>
              </View>
              <View style={[pdfStyles.tableHeaderCell, pdfStyles.colStatus]}>
                <PdfText>Status</PdfText>
              </View>
              <View style={[pdfStyles.tableHeaderCell, pdfStyles.colTitle]}>
                <PdfText>Titulo</PdfText>
              </View>
              <View style={[pdfStyles.tableHeaderCell, pdfStyles.colDescription]}>
                <PdfText>Descricao</PdfText>
              </View>
              <View style={[pdfStyles.tableHeaderCell, pdfStyles.colPhone]}>
                <PdfText>Telefone</PdfText>
              </View>
              <View style={[pdfStyles.tableHeaderCell, pdfStyles.colAddress]}>
                <PdfText>Endereco</PdfText>
              </View>
            </View>

            {pageTasks.map((task, taskIndex) => {
              const isLastRow = taskIndex === pageTasks.length - 1;

              return (
                <View
                  key={task.id}
                  style={isLastRow ? [pdfStyles.tableRow, pdfStyles.lastTableRow] : pdfStyles.tableRow}
                >
                  <View style={[pdfStyles.tableCell, pdfStyles.colSupporter]}>
                    <PdfText>{normalizePdfText(task.voter?.name)}</PdfText>
                  </View>
                  <View style={[pdfStyles.tableCell, pdfStyles.colStatus]}>
                    <PdfText>{getTaskStatusLabel(task.status)}</PdfText>
                  </View>
                  <View style={[pdfStyles.tableCell, pdfStyles.colTitle]}>
                    <PdfText>{normalizePdfText(task.title)}</PdfText>
                  </View>
                  <View style={[pdfStyles.tableCell, pdfStyles.colDescription]}>
                    <PdfText>{stripHtmlFromPdfText(task.description)}</PdfText>
                  </View>
                  <View style={[pdfStyles.tableCell, pdfStyles.colPhone]}>
                    <PdfText>{normalizePdfText(task.voter?.cellphone)}</PdfText>
                  </View>
                  <View style={[pdfStyles.tableCell, pdfStyles.colAddress]}>
                    <PdfText>{getTaskAddress(task)}</PdfText>
                  </View>
                </View>
              );
            })}
          </View>

          <PdfText style={pdfStyles.footer}>
            Pagina {pageIndex + 1} de {pages.length || 1}
          </PdfText>
        </Page>
      ))}
    </Document>
  );
}

export default function Demand() {
  const navigate = useNavigate();
  const toast = useToast();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { office, role } = useAuth();
  const canEdit = role?.demandas_page > 1;
  const [tasks, setTasks] = useState<TaskPropsDTO[]>([]);
  const [summary, setSummary] = useState<TaskSummaryDTO>({
    total: 0,
    backlog: 0,
    doing: 0,
    completed: 0,
    overdue: 0,
  });
  const [responsibles, setResponsibles] = useState<SelectOption[]>([]);
  const [selectedTask, setSelectedTask] = useState<TaskPropsDTO | null>(null);
  const [query, setQuery] = useState('');
  const [statusScope, setStatusScope] = useState<'all' | 'BACKLOG' | 'FAZENDO' | 'CONCLUIDO' | 'overdue' | 'open'>('all');
  const [priority, setPriority] = useState('all');
  const [responsibleId, setResponsibleId] = useState('all');
  const [startDeadline, setStartDeadline] = useState('');
  const [endDeadline, setEndDeadline] = useState('');
  const [sortValue, setSortValue] = useState('deadline:asc');
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
  const debouncedQuery = useDebounce(query, 400);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  const [sort_by, sort_order] = sortValue.split(':');

  const summaryCards = useMemo(
    () => [
      {
        key: 'all' as TaskTabKey,
        label: 'Total de demandas',
        value: summary.total,
        helper: 'Visão completa do gabinete',
        icon: FiFolder,
        accent: office?.primary_color || '#2563EB',
      },
      {
        key: 'BACKLOG' as TaskTabKey,
        label: 'Pendentes',
        value: summary.backlog,
        helper: 'Aguardando início',
        icon: FiClock,
        accent: '#D69E2E',
      },
      {
        key: 'FAZENDO' as TaskTabKey,
        label: 'Em andamento',
        value: summary.doing,
        helper: 'Em execução pela equipe',
        icon: FiLoader,
        accent: '#3182CE',
      },
      {
        key: 'CONCLUIDO' as TaskTabKey,
        label: 'Concluídas',
        value: summary.completed,
        helper: 'Finalizadas com registro',
        icon: FiCheckCircle,
        accent: '#2F855A',
      },
      {
        key: 'overdue' as TaskTabKey,
        label: 'Atrasadas',
        value: summary.overdue,
        helper: 'Precisam de atenção imediata',
        icon: FiAlertTriangle,
        accent: '#C53030',
      },
    ],
    [office?.primary_color, summary],
  );

  const fetchResponsibles = useCallback(async () => {
    try {
      const response = await api.get(
        `/permission/office/${role?.office_id}/responsible`,
      );

      setResponsibles(
        response.data.map((item: PermissionByIdDTO) => ({
          value: item.user.id as string,
          label: item.user.name,
        })),
      );
    } catch (error) {
      setResponsibles([]);
    }
  }, [role?.office_id]);

  const buildParams = useCallback((currentPage = page) => {
    const params: Record<string, string | number | undefined> = {
      page: currentPage,
      quantity: PER_PAGE,
      search: debouncedQuery || undefined,
      priority: priority !== 'all' ? priority : undefined,
      responsible_id: responsibleId !== 'all' ? responsibleId : undefined,
      start_deadline: startDeadline || undefined,
      end_deadline: endDeadline || undefined,
      sort_by,
      sort_order,
    };

    if (statusScope === 'overdue') {
      params.overdue = 'true';
    } else if (statusScope === 'open') {
      params.completed = 'false';
    } else if (statusScope !== 'all') {
      params.status = statusScope;
    }

    return params;
  }, [
    debouncedQuery,
    endDeadline,
    page,
    priority,
    responsibleId,
    sort_by,
    sort_order,
    startDeadline,
    statusScope,
  ]);

  const buildSummaryParams = useCallback(() => ({
    search: debouncedQuery || undefined,
    priority: priority !== 'all' ? priority : undefined,
    responsible_id: responsibleId !== 'all' ? responsibleId : undefined,
    start_deadline: startDeadline || undefined,
    end_deadline: endDeadline || undefined,
  }), [debouncedQuery, endDeadline, priority, responsibleId, startDeadline]);

  const fetchTasks = useCallback(async (currentPage = page) => {
    setLoading(true);
    setHasError(false);

    try {
      const response = await api.get(`/task/office/${office?.id}`, {
        params: buildParams(currentPage),
      });

      setTasks(response.data.items || []);
      setTotalItems(response.data.total || 0);
    } catch (error) {
      setHasError(true);
    } finally {
      setLoading(false);
    }
  }, [buildParams, office?.id, page]);

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);

    try {
      const response = await api.get(`/task/office/${office?.id}/summary`, {
        params: buildSummaryParams(),
      });
      setSummary(response.data);
    } catch (error) {
      setSummary({
        total: 0,
        backlog: 0,
        doing: 0,
        completed: 0,
        overdue: 0,
      });
    } finally {
      setSummaryLoading(false);
    }
  }, [buildSummaryParams, office?.id]);

  const refreshData = useCallback(async (currentPage = page) => {
    await Promise.all([fetchTasks(currentPage), fetchSummary()]);
  }, [fetchSummary, fetchTasks, page]);

  const fetchTaskDetails = useCallback(async (taskId: string) => {
    setDrawerLoading(true);
    onOpen();

    try {
      const response = await api.get(`/task/${taskId}`);
      setSelectedTask(response.data);
    } catch (error) {
      toast({
        title: 'Não foi possível carregar a demanda.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      onClose();
    } finally {
      setDrawerLoading(false);
    }
  }, [onClose, onOpen, toast]);

  const handleDeleteDialog = (taskId: string) => {
    setTaskToDelete(taskId);
    onDeleteOpen();
  };

  const handleDelete = async () => {
    if (!taskToDelete) {
      return;
    }

    setActionLoading(true);
    try {
      await api.delete(`/task/${taskToDelete}`);
      toast({
        title: 'Demanda excluída com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      if (selectedTask?.id === taskToDelete) {
        setSelectedTask(null);
        onClose();
      }
      await refreshData(1);
      setPage(1);
    } catch (error: any) {
      toast({
        title:
          error?.response?.data?.message ||
          'Ocorreu um erro ao excluir a demanda.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setActionLoading(false);
      setTaskToDelete(null);
      onDeleteClose();
    }
  };

  const handleQuickUpdate = async (
    taskId: string,
    payload: {
      status?: string;
      priority?: string;
      responsible_id?: string;
    },
  ) => {
    setActionLoading(true);
    try {
      await api.patch(`/task/${taskId}/quick`, payload);
      toast({
        title: 'Demanda atualizada',
        status: 'success',
        duration: 2500,
        isClosable: true,
        position: 'top-right',
      });
      await refreshData(page);
      if (selectedTask?.id === taskId) {
        await fetchTaskDetails(taskId);
      }
    } catch (error: any) {
      toast({
        title:
          error?.response?.data?.message ||
          'Não foi possível atualizar a demanda.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusChange = async (taskId: string, status: string) => {
    await handleQuickUpdate(taskId, { status });
  };

  const handlePrint = async () => {
    setPrintLoading(true);
    try {
      const params = buildParams(1);
      params.quantity = 100000;

      const response = await api.get(`/task/office/${office?.id}`, { params });
      const allTasks: TaskPropsDTO[] = response.data.items || [];

      const blob = await pdf(
        <CurrentTasksPdf
          officeName={office?.name || 'Gabinete'}
          tasks={allTasks}
        />,
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `demandas-${office?.name || 'gabinete'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: 'Não foi possível gerar o PDF.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setPrintLoading(false);
    }
  };

  const clearFilters = () => {
    setQuery('');
    setStatusScope('all');
    setPriority('all');
    setResponsibleId('all');
    setStartDeadline('');
    setEndDeadline('');
    setSortValue('deadline:asc');
    setPage(1);
  };

  useEffect(() => {
    if (office.id) {
      fetchResponsibles();
    }
  }, [fetchResponsibles, office.id]);

  useEffect(() => {
    if (!office.id) {
      return;
    }

    refreshData(page);
  }, [office.id, page, refreshData]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, statusScope, priority, responsibleId, startDeadline, endDeadline, sortValue]);

  const totalPages = totalItems;

  return (
    <HeaderSideBar>
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent mx={4}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Excluir demanda
            </AlertDialogHeader>
            <AlertDialogBody>
              Essa ação é irreversível. Deseja remover a demanda selecionada?
            </AlertDialogBody>
            <AlertDialogFooter>
              <ChakraButton
                bg="gray.100"
                color="gray.700"
                onClick={onDeleteClose}
                ref={cancelRef}
              >
                Cancelar
              </ChakraButton>
              <Button
                width="fit-content"
                bgColor="#DC2626"
                color="white"
                isLoading={actionLoading}
                onClick={handleDelete}
                ml={3}
              >
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <TaskDetailDrawer
        isOpen={isOpen}
        onClose={onClose}
        task={selectedTask}
        loading={drawerLoading}
        canEdit={canEdit}
        responsibles={responsibles}
        actionLoading={actionLoading}
        onEdit={taskId => navigate(`/demanda/${taskId}`)}
        onDelete={handleDeleteDialog}
        onQuickUpdate={handleQuickUpdate}
      />

      <Stack spacing={8}>
        <Flex
          direction={['column', 'column', 'row']}
          justify="space-between"
          align={['stretch', 'stretch', 'center']}
          gap={4}
        >
          <Box>
            <Text fontSize="xs" fontWeight="bold" letterSpacing="0.14em" color="gray.400">
              OPERAÇÃO DE DEMANDAS
            </Text>
            <Text mt={2} fontSize={['2xl', '3xl']} fontWeight="bold" color="gray.800">
              Gestão diária com foco em clareza e velocidade
            </Text>
            <Text mt={2} maxW="3xl" color="gray.500">
              Acompanhe prazos, responsáveis, prioridades e status em uma visão única, pronta para o uso diário da equipe.
            </Text>
          </Box>
          <Flex gap={3} wrap="wrap">
            <Button
              width="fit-content"
              bgColor="gray.100"
              color="gray.700"
              leftIcon={<FiRefreshCcw />}
              onClick={() => refreshData(page)}
              isLoading={loading || summaryLoading}
            >
              Atualizar
            </Button>
            <Button
              width="fit-content"
              bgColor="gray.100"
              color="gray.700"
              leftIcon={<FiPrinter />}
              onClick={handlePrint}
              isLoading={printLoading}
            >
              Imprimir
            </Button>
            {canEdit ? (
              <Button
                width="fit-content"
                leftIcon={<FiPlus />}
                onClick={() => navigate('/demanda/registrar-demanda')}
              >
                Nova demanda
              </Button>
            ) : null}
          </Flex>
        </Flex>

        <Grid templateColumns={['1fr', 'repeat(2, 1fr)', 'repeat(5, 1fr)']} gap={4}>
          {summaryCards.map(card => (
            <GridItem
              key={card.key}
              cursor="pointer"
              onClick={() => {
                if (card.key === 'all') {
                  setStatusScope('all');
                  return;
                }
                setStatusScope(card.key);
              }}
            >
              <Skeleton isLoaded={!summaryLoading}>
                <SummaryCard {...card} />
              </Skeleton>
            </GridItem>
          ))}
        </Grid>

        <Box
          rounded="3xl"
          borderWidth="1px"
          borderColor="gray.100"
          bg="linear-gradient(180deg, rgba(248,250,252,1) 0%, rgba(255,255,255,1) 100%)"
          p={[5, 6]}
          shadow="sm"
        >
          <Flex justify="space-between" align={['flex-start', 'center']} gap={4} direction={['column', 'row']}>
            <Box>
              <HStack spacing={2} color="gray.500">
                <Icon as={FiFilter} />
                <Text fontSize="sm" fontWeight="semibold">
                  Filtros e segmentação
                </Text>
              </HStack>
              <Text mt={1} color="gray.500">
                Refine a visão por título, descrição, apoiador, celular, responsável, prazo, prioridade e ordenação.
              </Text>
            </Box>
            <Button
              width="fit-content"
              bgColor="gray.100"
              color="gray.700"
              onClick={clearFilters}
            >
              Limpar filtros
            </Button>
          </Flex>

          <Grid templateColumns={['1fr', 'repeat(2, 1fr)', 'repeat(4, 1fr)']} gap={4} mt={6}>
            <GridItem colSpan={[1, 2, 2]}>
              <Input
                name="query"
                value={query}
                onChange={event => setQuery(event.target.value)}
                placeholder="Buscar por título, descrição, apoiador ou celular"
                leftIcon={<FiSearch />}
              />
            </GridItem>
            <Select
              bg="white"
              borderColor="gray.200"
              value={statusScope}
              onChange={event => setStatusScope(event.target.value as any)}
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <Select
              bg="white"
              borderColor="gray.200"
              value={priority}
              onChange={event => setPriority(event.target.value)}
            >
              {priorityOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <Select
              bg="white"
              borderColor="gray.200"
              value={responsibleId}
              onChange={event => setResponsibleId(event.target.value)}
            >
              <option value="all">Todos os responsáveis</option>
              {responsibles.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <Input
              name="startDeadline"
              type="date"
              value={startDeadline}
              onChange={event => setStartDeadline(event.target.value)}
            />
            <Input
              name="endDeadline"
              type="date"
              value={endDeadline}
              onChange={event => setEndDeadline(event.target.value)}
            />
            <Select
              bg="white"
              borderColor="gray.200"
              value={sortValue}
              onChange={event => setSortValue(event.target.value)}
            >
              {sortOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Grid>

          <HStack mt={6} spacing={3} overflowX="auto" pb={1}>
            {summaryCards.map(card => {
              const isActive =
                (card.key === 'all' && statusScope === 'all') ||
                statusScope === card.key;

              return (
                <Button
                  key={card.key}
                  width="fit-content"
                  bgColor={isActive ? office?.primary_color : 'white'}
                  color={isActive ? office?.secondary_color : 'gray.700'}
                  borderWidth="1px"
                  borderColor={isActive ? office?.primary_color : 'gray.200'}
                  onClick={() => {
                    if (card.key === 'all') {
                      setStatusScope('all');
                      return;
                    }
                    setStatusScope(card.key);
                  }}
                >
                  {card.label} ({card.value})
                </Button>
              );
            })}
          </HStack>
        </Box>

        <Box
          rounded="3xl"
          borderWidth="1px"
          borderColor="gray.100"
          bg="white"
          shadow="sm"
          overflow="hidden"
        >
          <Flex justify="space-between" align={['flex-start', 'center']} gap={4} px={[5, 6]} py={5} direction={['column', 'row']}>
            <Box>
              <Text fontSize="xl" fontWeight="bold" color="gray.800">
                Demandas em acompanhamento
              </Text>
              <Text color="gray.500">
                {totalItems} resultado(s) encontrados para a visão atual.
              </Text>
            </Box>
            <HStack spacing={3} color="gray.500">
              <HStack spacing={2}>
                <Icon as={sort_order === 'asc' ? FiArrowUp : FiArrowDown} />
                <Text fontSize="sm">
                  Ordenação {sort_order === 'asc' ? 'crescente' : 'decrescente'}
                </Text>
              </HStack>
            </HStack>
          </Flex>

          {hasError ? (
            <Box px={[5, 6]} pb={6}>
              <Box rounded="2xl" borderWidth="1px" borderColor="red.100" bg="red.50" px={5} py={6}>
                <Text fontWeight="bold" color="red.700">
                  Não foi possível carregar as demandas.
                </Text>
                <Text mt={1} color="red.600">
                  Tente novamente para restabelecer a listagem.
                </Text>
                <Button width="fit-content" mt={4} onClick={() => refreshData(page)}>
                  Tentar novamente
                </Button>
              </Box>
            </Box>
          ) : null}

          {!hasError && loading ? (
            <Stack spacing={4} px={[5, 6]} pb={6}>
              {[...Array(5)].map((_, index) => (
                <Skeleton key={index} h="82px" rounded="2xl" />
              ))}
            </Stack>
          ) : null}

          {!hasError && !loading && tasks.length === 0 ? (
            <Box px={[5, 6]} pb={6}>
              <Box rounded="2xl" borderWidth="1px" borderColor="gray.200" borderStyle="dashed" bg="gray.50" px={6} py={10} textAlign="center">
                <Text fontSize="lg" fontWeight="bold" color="gray.700">
                  Nenhuma demanda encontrada
                </Text>
                <Text mt={2} color="gray.500">
                  Ajuste os filtros ou cadastre uma nova demanda para começar.
                </Text>
                {canEdit ? (
                  <Button
                    width="fit-content"
                    mt={5}
                    onClick={() => navigate('/demanda/registrar-demanda')}
                  >
                    Criar demanda
                  </Button>
                ) : null}
              </Box>
            </Box>
          ) : null}

          {!hasError && !loading && tasks.length > 0 ? (
            <>
              <Box display={['none', 'none', 'block']}>
                <TableContainer>
                  <Table variant="simple">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th>Título</Th>
                        <Th>Status</Th>
                        <Th>Prioridade</Th>
                        <Th>Prazo</Th>
                        <Th>Responsável</Th>
                        <Th>Apoiador</Th>
                        <Th>Ações</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {tasks.map(task => {
                        const isOverdue =
                          !!task.deadline &&
                          task.status !== 'CONCLUIDO' &&
                          isPast(new Date(task.deadline)) &&
                          !isToday(new Date(task.deadline));

                        return (
                          <Tr key={task.id}>
                            <Td>
                              <Stack spacing={1}>
                                <Text fontWeight="semibold" color="gray.800">
                                  {task.title}
                                </Text>
                                <Text fontSize="sm" color="gray.500">
                                  Criada {formatDistanceToNowStrict(new Date(task.date), {
                                    addSuffix: true,
                                    locale: ptBR,
                                  })}
                                </Text>
                              </Stack>
                            </Td>
                            <Td>
                              <StatusBadge status={task.status} />
                            </Td>
                            <Td>
                              <PriorityBadge priority={task.priority} />
                            </Td>
                            <Td>
                              <Stack spacing={1}>
                                <Text color={isOverdue ? 'red.600' : 'gray.700'} fontWeight={isOverdue ? 'bold' : 'medium'}>
                                  {task.deadline
                                    ? getFormatDate(new Date(task.deadline), 'dd/MM/yyyy')
                                    : 'Sem prazo'}
                                </Text>
                                {isOverdue ? (
                                  <Text fontSize="xs" color="red.500">
                                    Atrasada
                                  </Text>
                                ) : null}
                              </Stack>
                            </Td>
                            <Td>
                              <HStack spacing={3}>
                                <Avatar size="sm" name={task.responsible?.name} src={task.responsible?.avatar_url} />
                                <Text color="gray.700">{task.responsible?.name || '-'}</Text>
                              </HStack>
                            </Td>
                            <Td>
                              <Text color="gray.700">{task.voter?.name || '-'}</Text>
                            </Td>
                            <Td>
                              <HStack spacing={2}>
                                <IconButton
                                  aria-label="Ver demanda"
                                  icon={<FiEye />}
                                  variant="ghost"
                                  onClick={() => fetchTaskDetails(task.id)}
                                />
                                <Select
                                  size="sm"
                                  maxW="150px"
                                  bg="gray.50"
                                  value={task.status}
                                  onChange={event => handleStatusChange(task.id, event.target.value)}
                                >
                                  <option value="BACKLOG">Pendente</option>
                                  <option value="FAZENDO">Em andamento</option>
                                  <option value="CONCLUIDO">Concluída</option>
                                </Select>
                                {canEdit ? (
                                  <>
                                    <IconButton
                                      aria-label="Editar demanda"
                                      icon={<FiEdit3 />}
                                      variant="ghost"
                                      onClick={() => navigate(`/demanda/${task.id}`)}
                                    />
                                    <IconButton
                                      aria-label="Excluir demanda"
                                      icon={<FiTrash2 />}
                                      variant="ghost"
                                      colorScheme="red"
                                      onClick={() => handleDeleteDialog(task.id)}
                                    />
                                  </>
                                ) : null}
                              </HStack>
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Box>

              <Stack display={['flex', 'flex', 'none']} spacing={4} px={5} pb={6}>
                {tasks.map(task => {
                  const isOverdue =
                    !!task.deadline &&
                    task.status !== 'CONCLUIDO' &&
                    isPast(new Date(task.deadline)) &&
                    !isToday(new Date(task.deadline));

                  return (
                    <Box
                      key={task.id}
                      rounded="2xl"
                      borderWidth="1px"
                      borderColor={isOverdue ? 'red.200' : 'gray.100'}
                      bg={isOverdue ? 'red.50' : 'white'}
                      p={4}
                    >
                      <Flex justify="space-between" align="flex-start" gap={3}>
                        <Box>
                          <Text fontWeight="bold" color="gray.800">
                            {task.title}
                          </Text>
                          <Text mt={1} fontSize="sm" color="gray.500">
                            {task.voter?.name || 'Sem apoiador'}
                          </Text>
                        </Box>
                        <StatusBadge status={task.status} />
                      </Flex>

                      <HStack mt={4} spacing={2} wrap="wrap">
                        <PriorityBadge priority={task.priority} />
                        <Badge rounded="full" colorScheme={isOverdue ? 'red' : 'purple'} px={3} py={1}>
                          {task.deadline
                            ? `Prazo ${getFormatDate(new Date(task.deadline), 'dd/MM/yyyy')}`
                            : 'Sem prazo'}
                        </Badge>
                      </HStack>

                      <Stack mt={4} spacing={2} color="gray.600" fontSize="sm">
                        <HStack>
                          <Icon as={FiUser} />
                          <Text>{task.responsible?.name || 'Sem responsável'}</Text>
                        </HStack>
                        <HStack>
                          <Icon as={FiCalendar} />
                          <Text>
                            Criada {formatDistanceToNowStrict(new Date(task.date), {
                              addSuffix: true,
                              locale: ptBR,
                            })}
                          </Text>
                        </HStack>
                      </Stack>

                      <Flex mt={4} gap={2} wrap="wrap">
                        <Button width="fit-content" leftIcon={<FiEye />} onClick={() => fetchTaskDetails(task.id)}>
                          Ver detalhes
                        </Button>
                        {canEdit ? (
                          <Button
                            width="fit-content"
                            bgColor="gray.100"
                            color="gray.700"
                            leftIcon={<FiEdit3 />}
                            onClick={() => navigate(`/demanda/${task.id}`)}
                          >
                            Editar
                          </Button>
                        ) : null}
                      </Flex>
                    </Box>
                  );
                })}
              </Stack>
            </>
          ) : null}
        </Box>

        {!hasError && totalItems > PER_PAGE ? (
          <Flex align="center" justify="center">
            <Pagination
              currentPage={page}
              perPage={PER_PAGE}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </Flex>
        ) : null}
      </Stack>
    </HeaderSideBar>
  );
}
